import {
  RiskType,
  SweepDestStatus,
  WalletType,
} from "@wallet-manager/node-types/dist/src/postgres/const";
import { usePageConfigProviderContext } from "src/features/PageConfigProvider";
import { useTranslation } from "src/hooks";
import { findChainInfo, listMappingTransform } from "src/utils/helper";
import { ViewGroupGetListRes } from "../types/ViewGroupGetListRes";
import { TableColumnKey } from "../../../types/Table";

interface UseTableRowsProps {
  apiList: ViewGroupGetListRes[];
}

type MapResultToTupleArray = (
  row: ViewGroupGetListRes[],
  omitKeys: string[],
) => any[][][];

const useTableRows = (props: UseTableRowsProps) => {
  const { translatePrefix } = usePageConfigProviderContext();
  const { t, te, translate } = useTranslation(translatePrefix);
  const { apiList } = props;

  const getRiskType = (riskType: number) => {
    return translate(`enumRiskType.${RiskType[riskType]}`);
  };

  const getSweepDestStatus = (status: number) => {
    return translate(`enumAutoSweepDestStatus.${SweepDestStatus[status]}`);
  };

  const getWalletType = (type: number | undefined) => {
    if (type === undefined) return "";
    return te(WalletType[type] as any);
  };

  const mapResultToTupleArray: MapResultToTupleArray = (rows, omitKeys) => {
    return rows.map((item) => {
      const mappedResult = [
        [
          TableColumnKey.ChainName,
          t(TableColumnKey.ChainName),
          findChainInfo(item.chain_type?.toString(), item.chain_id)?.name,
        ],
        [TableColumnKey.Currency, t(TableColumnKey.Currency), item.asset_name],
        [
          TableColumnKey.RiskType,
          t(TableColumnKey.RiskType),
          getRiskType(item.risk_type),
        ],
        [
          TableColumnKey.Status,
          t(TableColumnKey.Status),
          getSweepDestStatus(item.status),
        ],
        [
          TableColumnKey.WalletDisplayName,
          t(TableColumnKey.WalletDisplayName),
          item.wallet?.wallet_name,
        ],
        [
          TableColumnKey.RecipientAddress,
          t(TableColumnKey.RecipientAddress),
          item.wallet.wallet_address,
        ],
        [
          TableColumnKey.WalletType,
          t(TableColumnKey.WalletType),
          getWalletType(item.wallet?.wallet_type),
        ],
        [TableColumnKey.Rating, t(TableColumnKey.Rating), item.probability],
        [
          TableColumnKey.Probability,
          t(TableColumnKey.Probability),
          item.status === SweepDestStatus.Enable ? item.final_probability : "-",
        ],
        ["rawData", "", item],
      ].filter(([key]) => !omitKeys.includes(key as string));
      return mappedResult;
    });
  };

  const res = mapResultToTupleArray(apiList, []);
  const rows: any[] = res.map(listMappingTransform("key"));

  const lowRiskRecords = rows.filter(
    (row) => row.rawData.risk_type === RiskType.Low,
  );
  const highRiskRecords = rows.filter(
    (row) => row.rawData.risk_type === RiskType.High,
  );

  return {
    rows: {
      lowRisk: lowRiskRecords,
      highRisk: highRiskRecords,
    },
    totalCounts: {
      lowRisk: lowRiskRecords.length,
      highRisk: highRiskRecords.length,
    },
    mapResultToTupleArray,
  };
};

export default useTableRows;
